/**
 * Custom hook for managing selection mode state
 * Generic hook for handling multi-item selection with toggle, select all, and clear
 */

import { useState, useCallback } from 'react';

export interface UseSelectionModeReturn<T extends string> {
  /** Whether selection mode is active */
  isSelectionMode: boolean;
  /** Set of selected item IDs */
  selectedIds: Set<T>;
  /** Toggle selection mode on/off */
  toggleSelectionMode: () => void;
  /** Exit selection mode and clear selections */
  exitSelectionMode: () => void;
  /** Toggle selection for a specific item */
  toggleSelection: (id: T) => void;
  /** Select all items from provided list */
  selectAll: (ids: T[]) => void;
  /** Clear all selections without exiting selection mode */
  clearSelection: () => void;
  /** Check if an item is selected */
  isSelected: (id: T) => boolean;
  /** Number of currently selected items */
  selectionCount: number;
}

/**
 * Hook for managing selection mode state
 * @template T - Type of the item ID (typically string)
 * @returns Selection mode state and control functions
 */
export const useSelectionMode = <T extends string = string>(): UseSelectionModeReturn<T> => {
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<T>>(new Set());

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      const newMode = !prev;
      // Clear selections when exiting selection mode
      if (!newMode) {
        setSelectedIds(new Set());
      }
      return newMode;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelection = useCallback((id: T) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback((ids: T[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: T) => {
      return selectedIds.has(id);
    },
    [selectedIds]
  );

  const selectionCount = selectedIds.size;

  return {
    isSelectionMode,
    selectedIds,
    toggleSelectionMode,
    exitSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    selectionCount,
  };
};
